import React, { type ReactElement, type ReactNode, useEffect } from 'react';
import { Avatar, ListItem, Stack, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import { styled as materialStyled } from '@mui/material/styles';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as _ from 'lodash';
import { IconMenuItem, NestedMenuItem } from 'mui-nested-menu';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { CommonButton, CommonMenuItem, CommonModalV1, CommonTextField } from 'gemlux-common-components';
import { authActions } from '../../../../redux/auth/authSlice';
import { theme } from '../../../../theme/theme';
import { useAuth } from '../../../../context/AuthContext';
import { RootState } from '../../../../redux/store';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import AvatarEdit from '../../../AvatarEdit/AvatarEdit';
import LogoutBoldSvg from '../../../../assets/img/log-out-icon-red.svg';
import { getTitle, mapTitleToBeDisplayed, Titles } from '../../../../utils/general-layout-util';
import WarningSvg from '../../../../assets/img/warning.svg';
import NotificationSvg from '../../../../assets/img/notification-icon.svg';
import ProfileSvg from '../../../../assets/img/profile-icon-new.svg';
import LogoutSvg from '../../../../assets/img/logout-icon-new.svg';
import FilterSvg from '../../../../assets/img/V1/filter-horizontal-blue.svg';
import SearchSvg from '../../../../assets/img/search.svg';
import { CustomMenu } from '../../../CustomMenu/CustomMenu';
import {
  INVITATIONS_ROUTE,
  MANAGE_SUBSCRIPTION,
  GEM_GPT_ROUTE,
  MY_PROFILE_ROUTE,
  PAYMENTS_ROUTE,
  SUBORDINATES_ROUTE,
} from '../../../../utils/routes';
import { ActionType, SIDEBAR_OPTIONS, SidebarOptionType } from '../../../../utils/sidebar-schema';
import { UsageType } from '../../../../utils/enums/usage-type.enum';
import { gemActions } from '../../../../redux/gem/gemSlice';
import { NAVBAR_OPTIONS } from '../../../../utils/navbar-schema';
import useEnv from '../../../../utils/use-env-hook';
import CustomToolbar from '../../CustomToolbar/CustomToolbar';
import ToggleButtons from '../../ToggleButton/ToggleButton';
import { ViewType } from '../../../../utils/enums/view-type.enum';
import UploadGemModel from '../../UploadGemModel/UploadGemModel';
import { paymentActions } from '../../../../redux/payment/paymentSlice';
import CardIcon from '../../../../assets/img/V1/card_icon.svg';
import DisabledFilterIcon from '../../../../assets/img/V1/disabled-filter-icon.svg';
import DisabledSearchIcon from '../../../../assets/img/V1/blue-search-icon.svg';
import DisabledToggleButton from '../../../../assets/img/disabled-toggle-button.svg';
import DeleteGemModel from '../../DeleteGemModel/DeleteGemModel';
import SubordinateFilterModal from '../../../Subordinates/SubordinateNavbar/FilterComponent/FilterComponent';
import { Permission } from '../../../../utils/enums/permission.enum';
import { modelAction } from '../../../../redux/models/modelsSlice';
import { Roles } from '../../../../utils/enums/roles.enum';
import { subordinateActions } from '../../../../redux/subordinate/subordinateSlice';
import LogoLink from '../../../LogoLink/LogoLink';
import CustomButton from '../../CustomButton/CustomButton';
import EditGemConfirmationModel from '../../EditGemConfirmationModal/EditGemConfirmationModal';

interface BlankLayoutProps {
  children: ReactNode;
  backgroundColor?: string;
}

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = materialStyled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: theme.palette.white.main,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: '100%',
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

export const FooterNote = materialStyled(Typography)(() => ({
  textAlign: 'center',
  color: `${theme.palette.info.main}66`,
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  bottom: 0,
  padding: '12px 16px',
}));

const CustomContainer = materialStyled(Box)(() => ({
  overflow: 'auto',
  flexGrow: 1,
  backgroundColor: theme.palette.white.main,
  display: 'flex',
  justifyContent: 'center',
  padding: '16px 16px 56px 16px',
  marginTop: '100px',
  minHeight: 'calc(100vh - 100px)',
}));

const CustomList = materialStyled(List)(() => ({
  p: 0,
  flexDirection: 'row',
  display: 'flex',
  gap: '32px',
  whiteSpace: 'nowrap',
  alignItems: 'center',
}));

const MenuIconButtonStyle = materialStyled(IconButton)(() => ({
  zIndex: 999,
  paddingLeft: 0,
  padding: '1px',
  overflow: 'visible',
}));

const CustomNestedMenuItem = materialStyled(NestedMenuItem)(() => ({
  padding: '16px',
  width: '194px',
}));

const CustomIconMenuItem = materialStyled(IconMenuItem)(() => ({
  padding: '16px',
  width: '194px',
}));

function DefaultLayout({ children, backgroundColor }: BlankLayoutProps): ReactElement {
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { baseUrl } = useEnv();
  const navigate = useNavigate();

  const stripeAccountId = useAppSelector((state: RootState) => state.auth?.stripeAccountId);
  const { portalUrl, paymentMethodsLoading } = useAppSelector((state: RootState) => state.payment);
  const componentStatus = useAppSelector((state: RootState) => state.auth.componentStatus);
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
  const isLogoutModalOpen = _.get(componentStatus, 'isLogoutModalOpen', false) as boolean;
  const isLogoutProcessing = _.get(componentStatus, 'isLogoutProcessing', false) as boolean;
  const [subordinateFilterOpen, setSubordinateFilterOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [isImageLimitExceedModelOpen, setIsImageLimitExceedModelOpen] = React.useState(false);
  const [openSideBar, setOpenSideBar] = React.useState(false);
  const matchesMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const matchesMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const pathParts = location.pathname.split('/');
  const firstPathPart = `/${pathParts[1]}`;
  const title = getTitle(firstPathPart);
  const titleToBeDisplayed = mapTitleToBeDisplayed(title as Titles);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isViewPaymentButtonClicked, setIsPaymentButtonClicked] = React.useState(false);
  const [filterAndSearchDisabled, setFilterAndSearchDisabled] = React.useState(false);
  const [paymentButtonEnabled, setPaymentButtonEnabled] = React.useState(false);
  const [viewChangeDisabled, setViewChangeDisabled] = React.useState(false);
  const filterAndSearchDisabledPages = [
    PAYMENTS_ROUTE,
    MANAGE_SUBSCRIPTION,
    INVITATIONS_ROUTE,
    MY_PROFILE_ROUTE,
    GEM_GPT_ROUTE,
  ];
  const paymentMethodButtonEnablePages = [PAYMENTS_ROUTE];
  const viewChangeDisablePages = [SUBORDINATES_ROUTE, INVITATIONS_ROUTE];

  const handleCreatePortalSession = () => {
    if (stripeAccountId) {
      setIsPaymentButtonClicked(true);
      dispatch(
        paymentActions.createPortalSessionAsync({
          stripeAccountId,
          returnUrl: `${process.env.REACT_APP_BASE_URL}/payments`,
        }),
      );
    }
  };

  useEffect(() => {
    if (portalUrl && isViewPaymentButtonClicked) {
      window.open(portalUrl, '_blank');
    }
  }, [portalUrl]);

  useEffect(() => {
    if (matchesMdUp) {
      setOpenSideBar(true);
    }
    if (matchesMdDown) {
      setOpenSideBar(false);
    }
  }, [matchesMdUp, matchesMdDown]);

  useEffect(() => {
    if (filterAndSearchDisabledPages.includes(firstPathPart)) {
      setFilterAndSearchDisabled(true);
    } else {
      setFilterAndSearchDisabled(false);
    }
  }, [firstPathPart]);

  useEffect(() => {
    if (viewChangeDisablePages.includes(firstPathPart)) {
      setViewChangeDisabled(true);
    } else {
      setViewChangeDisabled(false);
    }
  }, [firstPathPart]);

  useEffect(() => {
    if (paymentMethodButtonEnablePages.includes(firstPathPart)) {
      setPaymentButtonEnabled(true);
    } else {
      setPaymentButtonEnabled(false);
    }
  }, [firstPathPart]);

  const handleOpenFilterModal = () => setSubordinateFilterOpen(true);
  const handleCloseFilterModal = () => setSubordinateFilterOpen(false);

  const debouncedSearch = React.useCallback(
    _.debounce((value) => {
      dispatch(subordinateActions.setFilters({ search: value }));
      dispatch(subordinateActions.fetchSubordinatesRequest());
    }, 1000),
    [],
  );

  const handleSearchChange = (event: { target: { value: string } }) => {
    const { value } = event.target;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleSuccessDismiss = () => {
    dispatch(authActions.clearComponentStatus());
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogOut = () => {
    dispatch(authActions.logOutAsync());
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const sideBarAction = (option: SidebarOptionType) => {
    setAnchorEl(null);
    switch (option.actionType) {
      case ActionType.PAGE_NAVIGATION:
        if (option.path === firstPathPart) {
          window.location.reload();
          break;
        }
        if (option.path) navigate(option.path);
        break;
      case ActionType.UPLOAD_PHOTO:
        if (user.usage.find((item: { type: UsageType }) => item.type === UsageType.IMAGE_STORAGE_OWN_DEVICE).exceeded) {
          setIsImageLimitExceedModelOpen(true);
        } else if (!user.permissions.includes(Permission.ADD_SELF_SCAN_GEMS) && user.role !== Roles.SAAS_ADMIN) {
          dispatch(modelAction.setIsPermissionDeniedModelOpen(true));
        } else {
          dispatch(gemActions.openAddGemModel());
        }
        break;
      default:
        break;
    }
  };
  const handleViewChange = (view: ViewType) => {
    dispatch(gemActions.resetGetGems());
    dispatch(gemActions.resetFavourites());
    switch (title) {
      case Titles.GemBank:
        return dispatch(gemActions.setComponentStatus({ view }));
      case Titles.MyGemVault:
        return dispatch(gemActions.setComponentStatus({ view }));
      case Titles.MyGemFav:
        return dispatch(gemActions.setComponentStatus({ view }));
      default:
    }
  };

  return (
    <Box display="flex" position="relative">
      <CssBaseline />
      <AppBar
        sx={{ border: 'none' }}
        elevation={0}
        variant="outlined"
        color="transparent"
        position="fixed"
        open={openSideBar}
      >
        <UploadGemModel />
        <EditGemConfirmationModel />
        <DeleteGemModel />
        <CustomToolbar>
          <Box display="flex" gap="20px">
            <LogoLink />
            <Typography variant="title3BoldInter" color="black" sx={{ display: { xs: 'none', sm: 'flex' } }}>
              {titleToBeDisplayed}
            </Typography>
          </Box>
          <Box flexGrow={1} />
          <Box sx={{ display: 'flex' }} gap={1} alignItems="center">
            <IconButton
              sx={{ padding: 0 }}
              onClick={() => {
                if (location.pathname === GEM_GPT_ROUTE) {
                  window.location.reload();
                } else {
                  navigate(GEM_GPT_ROUTE);
                }
              }}
            >
              <Box
                sx={{
                  border: `1px solid ${theme.palette.shades.lightBlueColor}`,
                  borderRadius: '8px',
                }}
                padding={1}
              >
                <Typography
                  variant="subheadRegular"
                  color={theme.palette.shades.lightBlueColor}
                  sx={{
                    display: {
                      xs: 'none',
                      sm: 'flex',
                    },
                  }}
                >
                  GemGPT™
                </Typography>
              </Box>
            </IconButton>
            <Divider orientation="vertical" flexItem sx={{ marginRight: 1.5, marginLeft: 1.5 }} />
            <IconButton sx={{ padding: 0 }} aria-label="show new notifications">
              <Box
                component="img"
                sx={{
                  border: `1px solid ${theme.palette.shades.lightBlueColor}`,
                  borderRadius: '8px',
                }}
                padding={1}
                src={NotificationSvg}
              />
            </IconButton>
            <Box flexGrow={0}>
              <Box
                display="flex"
                alignItems="center"
                sx={{
                  cursor: 'pointer',
                  border: `1px solid ${theme.palette.shades.lightBlueColor}`,
                  borderRadius: '100%',
                }}
                onClick={handleOpenUserMenu}
                data-testid="open-menu"
              >
                {user.avatar ? (
                  <Avatar
                    alt="Profile Image"
                    src={`${baseUrl}/${user.avatar}`}
                    sx={{ width: '38px', height: '38px', border: 'none', objectFit: 'cover', cursor: 'pointer' }}
                  />
                ) : (
                  <AvatarEdit
                    editable={false}
                    name={user.firstName}
                    name2={user.familyName}
                    font={theme.typography.footnoteBold}
                    size="38px"
                  />
                )}
              </Box>
              <CustomMenu
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                keepMounted
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
                sx={{ mt: 3 }}
              >
                <CommonMenuItem
                  onClick={() => {
                    if (location.pathname === MY_PROFILE_ROUTE) {
                      window.location.reload(); // Full page reload
                    } else {
                      navigate(MY_PROFILE_ROUTE);
                    }
                  }}
                >
                  <Box component="img" src={ProfileSvg} sx={{ marginRight: 1 }} />
                  <Typography variant="metaInter" color="info" textAlign="center">
                    My Profile
                  </Typography>
                </CommonMenuItem>
                <CommonMenuItem
                  onClick={() => {
                    dispatch(authActions.setComponentStatus({ isLogoutModalOpen: true }));
                    handleCloseUserMenu();
                  }}
                >
                  <Box component="img" src={LogoutSvg} sx={{ marginRight: 1 }} />
                  <Typography variant="metaInter" color="info" textAlign="center">
                    Log Out
                  </Typography>
                </CommonMenuItem>
              </CustomMenu>
            </Box>
          </Box>
        </CustomToolbar>
        <Divider orientation="horizontal" flexItem />
        <CustomToolbar minheight={52}>
          <Stack direction="row" alignItems="center" justifyContent="start" spacing={3} overflow="hidden">
            <Box display="flex" alignItems="center">
              <MenuIconButtonStyle color="inherit" aria-label="open drawer" onClick={handleOpenMenu}>
                {anchorEl ? <CloseIcon color="primary" sx={{ fontSize: '22px' }} /> : <MenuIcon />}
              </MenuIconButtonStyle>
              <Typography
                variant={anchorEl ? 'subheadBold' : 'subheadRegular'}
                color={anchorEl ? 'secondary.dark' : 'black'}
                sx={{
                  paddingLeft: 0.6,
                  display: { xs: 'none', sm: 'flex' },
                }}
              >
                Menu
              </Typography>
            </Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              boxShadow="0px 1px 2px 0px rgba(128, 128, 128, 0.12)"
            >
              <CommonTextField
                placeholder="Search"
                height={39}
                value={searchTerm}
                onChange={handleSearchChange}
                autoComplete="off"
                slotProps={{
                  input: {
                    startAdornment: (
                      <Box component="img" src={filterAndSearchDisabled ? DisabledSearchIcon : SearchSvg} />
                    ),
                  },
                }}
                sx={{
                  width: '19vw',
                  mt: '0.4rem',
                  display: { xs: 'none', sm: 'flex' },
                  input: {
                    '&::placeholder': {
                      fontFamily: 'Inter',
                      fontSize: '15px',
                      fontWeight: 400,
                      color: '#6F7C8E',
                    },
                  },
                }}
                disabled={filterAndSearchDisabled}
              />
            </Box>

            <Box
              alignItems="center"
              sx={{
                paddingRight: 1,
                paddingLeft: 1,
                gap: 2,
                border: filterAndSearchDisabled
                  ? '1px solid #EDF0F2'
                  : `1px solid ${theme.palette.shades.lightBlueColor}`,
                borderRadius: '8px',
                display: { xs: 'none', sm: 'flex' },
              }}
            >
              <Typography
                variant="textBase500"
                color={filterAndSearchDisabled ? '#EDF0F2' : theme.palette.shades.lightBlueColor}
                sx={{ display: { xs: 'none', md: 'none', sm: 'none', lg: 'flex' } }}
              >
                Filters
              </Typography>
              <IconButton
                color="inherit"
                aria-label="open filter"
                edge="start"
                onClick={handleOpenFilterModal}
                disabled={filterAndSearchDisabled}
              >
                {subordinateFilterOpen ? (
                  <CloseIcon color="primary" fontSize="small" />
                ) : (
                  <Box component="img" src={filterAndSearchDisabled ? DisabledFilterIcon : FilterSvg} />
                )}
              </IconButton>
            </Box>
            {paymentButtonEnabled && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <CommonButton
                  variant="text"
                  sx={{
                    padding: '8px 12px',
                    backgroundColor: '#E0EDFF',
                    borderRadius: '8px',
                  }}
                  loading={paymentMethodsLoading}
                  onClick={handleCreatePortalSession}
                  startIcon={<Box component="img" src={CardIcon} />}
                >
                  <Typography
                    variant="textBase500"
                    color={theme.palette.shades.primaryBrightDark}
                    sx={{
                      backgroundColor: '#E0EDFF',
                      display: { xs: 'none', md: 'none', sm: 'none', lg: 'flex' },
                    }}
                  >
                    View Payment Method
                  </Typography>
                </CommonButton>
              </Box>
            )}
          </Stack>
          <CustomMenu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
            sx={{ zIndex: 5 }}
          >
            {SIDEBAR_OPTIONS.filter((option) => option.roles.includes(user.role)).map((option) =>
              option.subPath && option.subPath?.length > 0 ? (
                <CustomNestedMenuItem
                  key={option.name}
                  leftIcon={
                    <Box
                      component="img"
                      src={
                        option.path && option.path === firstPathPart[1] ? option.icon.selected : option.icon.unselected
                      }
                    />
                  }
                  renderLabel={() => (
                    <Typography
                      variant={option.path && option.path === firstPathPart ? 'footnoteBold' : 'meta'}
                      color="black"
                      textAlign="center"
                      paddingLeft={0.5}
                    >
                      {option.name}
                    </Typography>
                  )}
                  parentMenuOpen={Boolean(anchorEl)}
                >
                  {option.subPath
                    .filter((subOption) => subOption.roles.includes(user.role))
                    .map((subOption) => (
                      <CustomIconMenuItem
                        key={subOption.name}
                        onClick={() => {
                          sideBarAction(subOption);
                        }}
                        disabled={user.isNccUser && user.role !== Roles.NCC_USER && subOption.disabled}
                        leftIcon={
                          <Box
                            component="img"
                            src={
                              subOption.path && subOption.path === firstPathPart[1]
                                ? subOption.icon.selected
                                : subOption.icon.unselected
                            }
                          />
                        }
                        renderLabel={() => (
                          <Typography
                            variant={subOption.path && subOption.path === firstPathPart ? 'footnoteBold' : 'meta'}
                            color={subOption.path && subOption.path === firstPathPart ? 'primary' : 'black'}
                            textAlign="center"
                            paddingLeft={0.5}
                          >
                            {subOption.name}
                          </Typography>
                        )}
                      />
                    ))}
                </CustomNestedMenuItem>
              ) : (
                <CustomIconMenuItem
                  key={option.name}
                  onClick={() => {
                    sideBarAction(option);
                  }}
                  disabled={user.isNccUser && option.disabled}
                  leftIcon={
                    <Box
                      component="img"
                      src={option.path === firstPathPart[1] ? option.icon.selected : option.icon.unselected}
                    />
                  }
                  renderLabel={() => (
                    <Typography
                      variant={option.path === firstPathPart ? 'footnoteBold' : 'meta'}
                      color="black"
                      textAlign="center"
                      paddingLeft={0.5}
                    >
                      {option.name}
                    </Typography>
                  )}
                />
              ),
            )}
          </CustomMenu>
          <SubordinateFilterModal open={subordinateFilterOpen} onClose={handleCloseFilterModal} />
          <Box flexGrow={1} />

          <CustomList>
            {Object.values(NAVBAR_OPTIONS)
              .filter((option) => option.roles.includes(user.role))
              .map((option) => (
                <Link
                  key={option.name}
                  to={option.path}
                  style={{ textDecoration: 'none', color: 'black' }}
                  onClick={() => {
                    if (firstPathPart === option.path) {
                      window.location.reload();
                    }
                  }}
                >
                  <ListItem key={option.name} disablePadding>
                    <Box key=" Invoice - Payment Status" display="flex" alignItems="center" gap={0.5}>
                      <Box
                        component="img"
                        src={option.path === firstPathPart ? option.icon.selected : option.icon.unselected}
                      />
                      <Typography
                        component="span"
                        variant={option.path === firstPathPart ? 'footnoteBold' : 'meta'}
                        color={option.path === firstPathPart ? 'primary' : 'black'}
                        sx={{ display: { xs: 'none', md: 'none', sm: 'none', lg: 'flex' } }}
                      >
                        {option.name}
                      </Typography>
                    </Box>
                  </ListItem>
                </Link>
              ))}
            <Divider orientation="vertical" flexItem />
            {filterAndSearchDisabled || viewChangeDisabled ? (
              <Box component="img" src={DisabledToggleButton} />
            ) : (
              <ToggleButtons changeView={handleViewChange} />
            )}
          </CustomList>
        </CustomToolbar>
        <Divider orientation="horizontal" flexItem />
      </AppBar>
      <CustomContainer sx={{ backgroundColor }}>{children}</CustomContainer>
      <FooterNote variant="subtitle2">&copy; {new Date().getFullYear()} Porolis Technologies Pte Ltd</FooterNote>
      <CommonModalV1
        modal
        open={isLogoutModalOpen}
        handleButtonOnClick={handleSuccessDismiss}
        title="Log Out Confirmation"
        Icon={<Box component="img" src={LogoutBoldSvg} />}
        subTitle="Are you sure you want to log out?"
      >
        <Grid container spacing={1.5} direction="column" mt={2.5}>
          <Grid item xs={12} sm={6}>
            <CustomButton
              loading={isLogoutProcessing}
              onClick={() => {
                dispatch(authActions.setComponentStatus({ isLogoutProcessing: true }));
                handleLogOut();
                handleCloseUserMenu();
              }}
              variant="contained"
              fullWidth
              color="error"
              sx={{ backgroundColor: theme.palette.shades.errorBrightMain }}
            >
              Yes, Log out
            </CustomButton>
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomButton
              variant="outlined"
              onClick={() => {
                dispatch(authActions.setComponentStatus({ isLogoutModalOpen: false }));
              }}
              fullWidth
            >
              No
            </CustomButton>
          </Grid>
        </Grid>
      </CommonModalV1>
      <CommonModalV1
        modal
        open={isImageLimitExceedModelOpen}
        handleButtonOnClick={handleSuccessDismiss}
        title="Limit Reached"
        Icon={<Box component="img" src={WarningSvg} />}
        subTitle="If you need to upload, further, then considering deleting the pictures"
      >
        <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
          <CommonButton
            variant="outlined"
            sx={{ width: '150px' }}
            onClick={() => setIsImageLimitExceedModelOpen(false)}
          >
            Dismiss
          </CommonButton>
        </Box>
      </CommonModalV1>
    </Box>
  );
}

export default DefaultLayout;
